/**
 * AppraisalQueueScreen (/appraisal-queue) — the internal-appraisal team's
 * worklist for the "Order Internal Appraisal Review" flow.
 *
 * INTERNAL STAFF ONLY (underwriting / admin). Loan-side users place orders from
 * the loan's Orders tab; this is where the appraisal team claims and works them:
 * claim → mark in review → complete (with an outcome) / cancel. Contract:
 * eastview-os/frontend_documentation/internal-appraisal-orders.md §B.
 *
 * One fetch of the full queue; lanes / "my queue" / search are filtered
 * client-side. Each action returns the updated order and patches its row in
 * place; a 409 (someone else transitioned it) reloads the queue instead.
 *
 * The second tab, `outside/*`, lists outside party (vendor / AMC) orders across
 * all loans — see OutsideOrdersQueue.js. It reuses this screen's loan index.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Route,
  Routes,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useSnackbar } from "notistack";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry, SetFilterModule } from "ag-grid-enterprise";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import ArrowBackRounded from "@mui/icons-material/ArrowBackRounded";
import GavelOutlined from "@mui/icons-material/GavelOutlined";
import OpenInNewRounded from "@mui/icons-material/OpenInNewRounded";

import AgGridTableWrapper from "../../components/agGrid/AgGridTableWrapper";
import {
  INACTIVE_TOOLTIP,
  PillGroup,
  QueueScopeRow,
  QueueStatusBar,
  ToolbarSwitch,
  dateOnlyComparator,
  dealKey,
  fetchLoanIndex,
  loanStageColumnDefs,
  readHideInactive,
  withLoanStage,
  writeHideInactive,
} from "../../components/queueToolbar/queueToolbar";
import InputWrapper from "../../components/inputs/shared/InputWrapper";
import { AppraisalOrderDetailContent } from "../../components/appraisalOrders/AppraisalOrderDetailDrawer";
import {
  APPRAISAL_STATUS_META,
  orderAppraisalDocOids,
} from "../../components/appraisalOrders/appraisalOrderUi";
import { usePermissionsHook } from "../../hooks/usePermissionsHook";
import { useLoanFilesHook } from "../../hooks/useLoanFilesHook";
import { useUnderwritingHook } from "../../hooks/useUnderwritingHook";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { formatDate } from "../../_src_shared/utils/functions/formatDate";
import { status as statusTones } from "../../theme/colors";
import LoanDrilldownScreen from "../LoanDrilldownScreen/LoanDrilldownScreen";
import {
  AppraisalQueueTabs,
  correspondentColumnDef,
  RefreshButton,
  SearchBox,
  QUEUE_MAX_WIDTH,
  StatCard,
  T,
} from "./appraisalQueueAtoms";
import OutsideOrdersQueue from "./OutsideOrdersQueue";

// Set filters (Status / Loan stage / Assigned to / Outcome floating filters).
ModuleRegistry.registerModules([SetFilterModule]);

const ALL = "__all__";

// Workflow statuses → lane order + badge colors (per the §B spec).
const STATUS_META = {
  queued: { label: "Queued", fg: "#475569", bg: "#eef2f4", dot: "#94a3b8" },
  assigned: { label: "Assigned", fg: "#1d4ed8", bg: "#e8efff", dot: "#3b82f6" },
  in_review: {
    label: "In review",
    fg: "#92600a",
    bg: "#fef3e2",
    dot: "#d97f0d",
  },
  completed: {
    label: "Completed",
    fg: "#0f6e56",
    bg: "#e1f5ee",
    dot: "#10916f",
  },
  cancelled: {
    label: "Cancelled",
    fg: "#a32d2d",
    bg: "#f5eceb",
    dot: "#c0716e",
  },
};

const LANES = [
  { key: ALL, label: "All" },
  { key: "queued", label: "Queued" },
  { key: "assigned", label: "Assigned" },
  { key: "in_review", label: "In review" },
  { key: "completed", label: "Completed" },
  { key: "cancelled", label: "Cancelled" },
];

// Status row pills (bottom) — same lanes, with each status's dot color.
const LANE_PILLS = LANES.map((l) => ({
  ...l,
  dot: l.key === ALL ? null : STATUS_META[l.key]?.dot,
}));

// Pipeline scope row (top). Which pipeline an order's loan lives in.
const SCOPE = { ALL: "all", PRECLOSE: "preclose", ACQ: "acquisition" };
const SCOPE_PILLS = [
  { key: SCOPE.ALL, label: "All" },
  { key: SCOPE.PRECLOSE, label: "Pre-Close", dot: statusTones.info.solid },
  { key: SCOPE.ACQ, label: "Acquisition", dot: statusTones.success.solid },
];
const inScope = (o, scope) =>
  scope === SCOPE.ALL ||
  (scope === SCOPE.ACQ ? o.loanIsAcquisition : !o.loanIsAcquisition);

const OUTCOME_OPTIONS = [
  { value: "approved", label: "Approved" },
  { value: "approved_with_conditions", label: "Approved with conditions" },
  { value: "revision_requested", label: "Revision requested" },
  { value: "rejected", label: "Rejected" },
];
const OUTCOME_LABEL = OUTCOME_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

const TERMINAL = new Set(["completed", "cancelled"]);
const orderKey = (o) => o?._id || o?.id;

// ObjectIds may arrive as strings or { $oid } objects — compare by value.
const sameId = (a, b) => {
  const s = (v) => (v && typeof v === "object" ? v.$oid || v._id : v);
  return s(a) != null && s(a) === s(b);
};

const matchesSearch = (o, q) => {
  if (!q) return true;
  const hay = `${o?.subjectPropertyAddress || ""} ${o?.loanNumber || ""} ${
    o?.requestedBy || ""
  } ${o?.assignedTo || ""} ${o?.loanStage || ""} ${
    o?._loan?.originator || ""
  }`.toLowerCase();
  return hay.includes(q);
};

const HIDE_INACTIVE_KEY = "appraisalQueue.hideInactiveLoans";

/* ── small UI atoms ─────────────────────────────────────────────────────── */

const StatusChip = ({ status }) => {
  const m = STATUS_META[status] || {
    label: status || "—",
    fg: T.ink500,
    bg: "#eef2f4",
    dot: T.ink300,
  };
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        fontSize: 12,
        fontWeight: 600,
        color: m.fg,
        background: m.bg,
        borderRadius: "6px",
        px: "9px",
        py: "3px",
        height: "32px",
        whiteSpace: "nowrap",
      }}
    >
      <Box
        component="span"
        sx={{ width: 6, height: 6, borderRadius: "50%", background: m.dot }}
      />
      {m.label}
    </Box>
  );
};

const EmptyState = ({ hasAny }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 1,
      py: 8,
      px: 4,
      textAlign: "center",
    }}
  >
    <GavelOutlined sx={{ fontSize: 34, color: T.ink300 }} />
    <Box sx={{ fontSize: 15, fontWeight: 700, color: T.ink900 }}>
      {hasAny ? "No orders match this view" : "No appraisal reviews yet"}
    </Box>
    <Box sx={{ fontSize: 13, color: T.ink500, maxWidth: 380 }}>
      {hasAny
        ? "Try a different lane, clear the search, or turn off “My Queue”, or include Dead / Inactive / Closed Loans."
        : "Orders placed from a loan's Orders tab appear here for the internal-appraisal team."}
    </Box>
  </Box>
);

const NoAccessGate = () => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 1.25,
      minHeight: "60vh",
      textAlign: "center",
      color: T.ink500,
      p: 4,
    }}
  >
    <GavelOutlined sx={{ fontSize: 36, color: T.ink300 }} />
    <Box sx={{ fontSize: 16, fontWeight: 700, color: T.ink900 }}>
      Internal appraisal team only
    </Box>
    <Box sx={{ fontSize: 13.5, maxWidth: 440 }}>
      The appraisal review queue is restricted to internal underwriting / admin
      staff.
    </Box>
  </Box>
);

/* ── row actions (assign / in-review / complete / cancel) ───────────────── */

const RowActions = ({ order, busy, onAction }) => {
  const status = order?.status;
  if (TERMINAL.has(status)) {
    return (
      <Box
        component="span"
        sx={{ color: T.ink300, fontStyle: "italic", fontSize: 12.5 }}
      >
        {status === "completed" ? "Done" : "Cancelled"}
      </Box>
    );
  }

  const ActBtn = ({ label, kind = "outlined", color = "primary", onClick }) => (
    <Button
      size="medium"
      variant={kind}
      color={color}
      disabled={busy}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      sx={{
        flexGrow: 1,
        textTransform: "none",
        fontSize: 11.5,
        fontWeight: 600,
        whiteSpace: "nowrap",
        minWidth: 0,
      }}
    >
      {label}
    </Button>
  );

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        flexWrap: "wrap",
        rowGap: 0.75,
      }}
    >
      <ActBtn
        label="Cancel"
        color="error"
        onClick={() => onAction("cancel", order)}
      />
      {status === "queued" && (
        <ActBtn
          label="Assign to me"
          kind="contained"
          onClick={() => onAction("assign", order)}
        />
      )}
      {(status === "assigned" || status === "queued") && (
        <ActBtn
          label="In review"
          onClick={() => onAction("in-review", order)}
        />
      )}
      {(status === "assigned" || status === "in_review") && (
        <ActBtn
          label="Complete"
          kind="contained"
          onClick={() => onAction("complete", order)}
        />
      )}
    </Box>
  );
};

/* ── confirmation dialog (every action) ─────────────────────────────────── */

// Per-action copy so each button explains exactly what it does before it fires.
const ACTION_META = {
  assign: {
    title: "Assign this review to you?",
    description:
      "Claims the order and assigns it to you. Its status moves to “Assigned” and it leaves the unclaimed queue so other reviewers know you've got it.",
    confirmLabel: "Assign to me",
    confirmColor: "primary",
  },
  "in-review": {
    title: "Mark this review as in progress?",
    description:
      "Sets the order to “In review” to signal you've started working the file. The requester can see it's actively underway.",
    confirmLabel: "Mark in review",
    confirmColor: "primary",
  },
  complete: {
    title: "Complete this review?",
    description:
      "Finalizes the review with the outcome below and notifies the requester. This closes the order — it can't be reopened from here.",
    confirmLabel: "Mark completed",
    confirmColor: "primary",
  },
  cancel: {
    title: "Cancel this order?",
    description:
      "Cancels the order so no review will be performed. This can't be undone, though the requester can place a new order if needed.",
    confirmLabel: "Cancel order",
    confirmColor: "error",
  },
};

// The queue can launch this dialog on top of the loan-preview drawer, so the dialog
// is pushed above that drawer's z (13400). Anything the dialog pops open (the outcome
// Select's menu) defaults to MUI's modal z of 1300 and would paint BEHIND the dialog
// paper — it has to be lifted past DIALOG_Z explicitly.
const DIALOG_Z = 13500;
const MENU_PROPS = { sx: { zIndex: DIALOG_Z + 1 } };

const ActionDialog = ({ mode, order, submitting, onClose, onSubmit }) => {
  const [outcome, setOutcome] = useState("");
  const [notes, setNotes] = useState("");
  const [reason, setReason] = useState("");

  // Reset fields whenever a new order/mode is opened.
  useEffect(() => {
    setOutcome("");
    setNotes("");
    setReason("");
  }, [order, mode]);

  if (!order || !mode) return null;
  const meta = ACTION_META[mode] || {};
  const isComplete = mode === "complete";
  const isCancel = mode === "cancel";
  const canSubmit = isComplete ? Boolean(outcome) : true;

  const buildPayload = () => {
    if (isComplete) return { reviewOutcome: outcome, reviewNotes: notes };
    if (isCancel) return { cancelReason: reason };
    return {};
  };

  return (
    <Dialog
      open
      onClose={submitting ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      // Sit above the detail drawer (z 13400) when launched from the preview.
      sx={{ zIndex: DIALOG_Z }}
    >
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>
        {meta.title}
      </DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          pt: "8px !important",
        }}
      >
        <Box sx={{ fontSize: 12.5, color: T.ink500 }}>
          {order.subjectPropertyAddress || "Subject property"}
          {order.loanNumber != null ? ` · Loan #${order.loanNumber}` : ""}
        </Box>

        <Box sx={{ fontSize: 13, color: T.ink700, lineHeight: 1.5 }}>
          {meta.description}
        </Box>

        {isComplete && (
          <>
            <InputWrapper label="Review outcome" required fullWidth>
              <TextField
                select
                size="small"
                value={outcome}
                onChange={(e) => setOutcome(e.target.value)}
                fullWidth
                SelectProps={{ MenuProps: MENU_PROPS }}
              >
                {OUTCOME_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            </InputWrapper>
            <InputWrapper label="Review notes (optional)" fullWidth>
              <TextField
                size="small"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                fullWidth
                multiline
                minRows={3}
              />
            </InputWrapper>
          </>
        )}

        {isCancel && (
          <InputWrapper label="Cancel reason (optional)" fullWidth>
            <TextField
              size="small"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
          </InputWrapper>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          disabled={submitting}
          sx={{ textTransform: "none" }}
          color="inherit"
        >
          Go back
        </Button>
        <Button
          variant="contained"
          color={meta.confirmColor || "primary"}
          disabled={!canSubmit || submitting}
          loading={submitting}
          onClick={() => onSubmit(buildPayload())}
          sx={{ textTransform: "none" }}
        >
          {meta.confirmLabel || "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ── full table view (index route) ──────────────────────────────────────── */

const FullQueueView = ({
  orders,
  filtered,
  stats,
  laneCounts,
  lane,
  setLane,
  search,
  setSearch,
  myQueue,
  setMyQueue,
  hideInactive,
  setHideInactive,
  hiddenCount,
  scope,
  setScope,
  scopeCounts,
  loading,
  error,
  columnDefs,
  onRefresh,
  onSelect,
}) => (
  <Box
    sx={{
      background: T.bg,
      // Grow with the content inside the flex-column dashboard main —
      // minHeight:100% alone stops the gray at the viewport and leaves the
      // area beside a tall table white (same fix as the Lock Queue).
      flex: "1 0 auto",
      minHeight: "100%",
      fontFamily: "inherit",
    }}
  >
    <Container
        maxWidth={false}
        sx={{ maxWidth: QUEUE_MAX_WIDTH, pt: "36px", pb: "72px" }}
      >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
        <Box>
          <Box
            sx={{
              fontSize: 23,
              fontWeight: 700,
              letterSpacing: "-.01em",
              color: T.ink900,
            }}
          >
            Appraisal Queue
          </Box>
          <Box sx={{ color: T.ink500, mt: "5px", fontSize: 13.5 }}>
            Claim, review, and complete internal appraisal reviews, and track
            external orders, across all loans.
          </Box>
        </Box>
        <Box sx={{ ml: "auto" }}>
          <RefreshButton onClick={onRefresh} loading={loading} />
        </Box>
      </Box>
      <AppraisalQueueTabs />

      {/* Top — pipeline scope + switches */}
      <QueueScopeRow label="Pipeline">
        <PillGroup
          options={SCOPE_PILLS}
          value={scope}
          onChange={setScope}
          counts={scopeCounts}
        />
        <ToolbarSwitch checked={myQueue} onChange={setMyQueue} label="My Queue" />
        <ToolbarSwitch
          checked={!hideInactive}
          onChange={(on) => setHideInactive(!on)}
          label="Include Dead / Inactive / Closed Loans"
          note={hideInactive && hiddenCount > 0 ? `(${hiddenCount} Hidden)` : null}
          tooltip={INACTIVE_TOOLTIP}
        />
      </QueueScopeRow>

      {/* Middle — widgets */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(2,1fr)", md: "repeat(4,1fr)" },
          gap: 2,
          m: "18px 0",
        }}
      >
        <StatCard n={stats.queued} label="Queued" color="#94a3b8" />
        <StatCard
          n={stats.active}
          label="Assigned / in review"
          color="#d97f0d"
        />
        <StatCard n={stats.mine} label="Assigned to me" color={T.teal600} />
        <StatCard n={stats.completed} label="Completed" color="#10916f" />
      </Box>

      {/* Bottom — status pills + count + search */}
      <QueueStatusBar
        pills={
          <PillGroup
            options={LANE_PILLS}
            value={lane}
            onChange={setLane}
            counts={laneCounts}
          />
        }
        shown={filtered.length}
        total={scopeCounts[scope] ?? 0}
        search={search}
        onSearch={setSearch}
        placeholder="Search loan #, property, people…"
      />

      <Box
        sx={{
          background: T.card,
          border: `1px solid ${T.line}`,
          borderRadius: T.radius,
          boxShadow: T.shadow,
          overflow: "hidden",
        }}
      >
        {error ? (
          <Box sx={{ p: 4, color: T.red, fontSize: 13.5 }}>{error}</Box>
        ) : loading ? (
          <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
            <CircularProgress size={26} />
          </Box>
        ) : filtered.length === 0 ? (
          <EmptyState hasAny={orders.length > 0} />
        ) : (
          <AgGridTableWrapper height="auto" noBorder noBorderRadius>
            <AgGridReact
              rowData={filtered}
              columnDefs={columnDefs}
              defaultColDef={{
                resizable: true,
                sortable: true,
                suppressHeaderMenuButton: true,
                floatingFilter: true,
                minWidth: 90,
                cellStyle: { display: "flex", alignItems: "center" },
              }}
              getRowId={(p) => orderKey(p.data)}
              domLayout="autoHeight"
              pagination
              paginationPageSize={25}
              rowHeight={56}
              headerHeight={40}
              floatingFiltersHeight={38}
              suppressCellFocus
              rowStyle={{ cursor: "pointer" }}
              onCellClicked={(e) => {
                // Open the split view except when clicking the loan deep-link.
                if (e.colDef?.headerName === "Loan") return;
                if (e.data) onSelect(e.data);
              }}
            />
          </AgGridTableWrapper>
        )}
      </Box>
    </Container>
  </Box>
);

/* ── compact list row (split view, column 1) ────────────────────────────── */

const CompactOrderRow = ({ order, selected, onClick }) => {
  const meta = APPRAISAL_STATUS_META[order?.status] || {
    dot: "#94a3b8",
    label: order?.status || "—",
  };
  const due = order?.requestedByDate
    ? formatDate(order.requestedByDate, "")
    : null;
  return (
    <Box
      onClick={onClick}
      sx={{
        cursor: "pointer",
        p: "10px 13px",
        borderBottom: `1px solid ${T.line}`,
        borderLeft: `2px solid ${selected ? meta.dot : "transparent"}`,
        background: selected ? "#f7f9fa" : "transparent",
        "&:hover": { background: "#f7f9fa" },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: "3px",
        }}
      >
        <Box
          sx={{
            fontSize: 12.5,
            fontWeight: 600,
            color: T.ink900,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
          }}
        >
          {order?.subjectPropertyAddress || "Subject property"}
        </Box>
        <Box
          sx={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            background: meta.dot,
            flexShrink: 0,
          }}
        />
      </Box>
      <Box
        sx={{
          fontSize: 11,
          color: T.ink500,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {[
          meta.label,
          order?.loanNumber != null ? `#${order.loanNumber}` : null,
          due ? `due ${due}` : null,
        ]
          .filter(Boolean)
          .join(" · ")}
      </Box>
    </Box>
  );
};

/* ── split view (`:id/*`) — list rail + order detail + embedded drilldown ── */

const SplitQueueView = ({
  orders,
  filtered,
  lane,
  setLane,
  search,
  setSearch,
  loading,
  laneCounts,
  actingId,
  onAction,
  onSelect,
  onClose,
}) => {
  const { id: dealId } = useParams();
  const [params] = useSearchParams();
  const orderId = params.get("order");
  const loanDrilldown = useSelector((s) => s.loanDrilldown);
  const { getFileById } = useLoanFilesHook();
  const [previewing, setPreviewing] = useState(false);

  const selectedOrder = useMemo(
    () =>
      (orderId && orders.find((o) => orderKey(o) === orderId)) ||
      orders.find((o) => sameId(o?.dealId, dealId)) ||
      null,
    [orders, orderId, dealId],
  );

  // The embedded drilldown (column 3) loads the loan into Redux; resolve every
  // appraisal doc on the order from there once it matches the selected deal
  // (an order can carry several appraisals).
  const loanLoaded = sameId(loanDrilldown?._id, dealId);
  const appraisalDocOids = useMemo(
    () => orderAppraisalDocOids(selectedOrder),
    [selectedOrder],
  );
  const appraisalDocs = useMemo(() => {
    if (!loanLoaded) return [];
    const docs = loanDrilldown?.loanDocuments || [];
    return appraisalDocOids
      .map((oid) => docs.find((d) => sameId(d?._id, oid)) || null)
      .filter(Boolean);
  }, [loanLoaded, loanDrilldown, appraisalDocOids]);

  // `previewing` holds the oid being fetched so only that card spins.
  const handlePreview = useCallback(
    (doc) => {
      if (!doc?.file_storage_name || !dealId) return;
      const oid = String(doc?._id?.$oid || doc?._id || "");
      setPreviewing(oid || true);
      getFileById({
        loanId: dealId,
        file_storage_name: doc.file_storage_name,
        fileDownloadName: doc.file_display_name,
        isFile: true,
        openInNewTab: true,
        onSuccessFn: () => setPreviewing(false),
        onFailFn: () => setPreviewing(false),
      });
    },
    [dealId, getFileById],
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: "1 1 0",
        minHeight: 0,
        height: "100%",
        width: "100%",
        background: T.bg,
      }}
    >
      <Box sx={{ display: "flex", flex: "1 1 0", minHeight: 0 }}>
        {/* Column 1 — list rail */}
        <Box
          sx={{
            width: 248,
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            background: "#fff",
            borderRight: `1px solid ${T.line}`,
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              height: 44,
              borderBottom: `1px solid ${T.line}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "6px",
              px: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
              <Tooltip title="Back to queue" arrow>
                <Box
                  component="button"
                  type="button"
                  onClick={onClose}
                  aria-label="Back to queue"
                  sx={{
                    font: "inherit",
                    color: T.teal600,
                    background: "transparent",
                    border: "none",
                    p: 0,
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    "&:hover": { opacity: 0.75 },
                  }}
                >
                  <ArrowBackRounded sx={{ fontSize: 16 }} />
                </Box>
              </Tooltip>
              <Select
                value={lane}
                onChange={(e) => setLane(e.target.value)}
                variant="standard"
                disableUnderline
                renderValue={(v) =>
                  v === ALL
                    ? "All"
                    : LANES.find((l) => l.key === v)?.label || "All"
                }
                sx={{
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: T.teal600,
                  "& .MuiSelect-select": {
                    p: 0,
                    pr: "18px !important",
                    minHeight: "unset",
                    color: T.teal600,
                  },
                  "& .MuiSelect-icon": { color: T.teal600, right: -2 },
                  "&:before, &:after": { display: "none" },
                }}
              >
                {LANES.map(({ key, label }) => (
                  <MenuItem key={key} value={key} sx={{ fontSize: 13 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        gap: 1,
                      }}
                    >
                      <span>{label}</span>
                      <Box
                        component="span"
                        sx={{
                          ml: "auto",
                          fontSize: 11,
                          color: T.ink500,
                          background: "#f1f4f6",
                          borderRadius: "6px",
                          px: "8px",
                          py: "2px",
                        }}
                      >
                        {laneCounts[key] ?? 0}
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Box component="span" sx={{ fontSize: 12, color: T.ink500 }}>
              {filtered.length}
            </Box>
          </Box>

          <Box
            sx={{
              p: "10px 12px",
              borderBottom: `1px solid ${T.line}`,
              flexShrink: 0,
            }}
          >
            <SearchBox value={search} onChange={setSearch} />
          </Box>

          <Box sx={{ flex: "1 1 0", minHeight: 0, overflowY: "auto" }}>
            {loading ? (
              <Box sx={{ display: "grid", placeItems: "center", py: 6 }}>
                <CircularProgress size={22} />
              </Box>
            ) : filtered.length === 0 ? (
              <EmptyState hasAny={orders.length > 0} />
            ) : (
              filtered.map((o) => (
                <CompactOrderRow
                  key={orderKey(o)}
                  order={o}
                  selected={orderKey(o) === orderKey(selectedOrder)}
                  onClick={() => onSelect(o)}
                />
              ))
            )}
          </Box>
        </Box>

        {/* Column 2 — order detail */}
        <Box
          sx={{
            width: 380,
            flexShrink: 0,
            minHeight: 0,
            overflowY: "auto",
            background: "#fff",
            borderRight: `1px solid ${T.line}`,
          }}
        >
          {selectedOrder ? (
            <AppraisalOrderDetailContent
              order={selectedOrder}
              showLoanLink={false}
              appraisalDocs={appraisalDocs}
              docLoading={
                appraisalDocOids.length > 0 && !loanLoaded
              }
              onPreviewAppraisal={handlePreview}
              previewingAppraisal={previewing}
              actions={
                !TERMINAL.has(selectedOrder.status) ? (
                  <RowActions
                    order={selectedOrder}
                    busy={actingId === orderKey(selectedOrder)}
                    onAction={onAction}
                  />
                ) : null
              }
            />
          ) : (
            <Box
              sx={{ p: 4, textAlign: "center", color: T.ink300, fontSize: 13 }}
            >
              Select an order to see its details.
            </Box>
          )}
        </Box>

        {/* Column 3 — embedded loan drilldown */}
        <Box
          sx={{
            position: "relative",
            flex: "1 1 0",
            minWidth: 0,
            height: "100%",
            overflow: "hidden",
            background: "#f1f5f9",
          }}
        >
          <LoanDrilldownScreen embedded />
        </Box>
      </Box>
    </Box>
  );
};

/* ── screen ─────────────────────────────────────────────────────────────── */

const AppraisalQueueScreen = () => {
  useDocumentTitle("Appraisal Queue");
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { isUnderwriter, isAdmin } = usePermissionsHook();
  const user = useSelector((state) => state.user);
  const myEmail = (user?.emailAddress || user?.email || "").toLowerCase();
  const canAccess = isUnderwriter || isAdmin;

  const {
    getAppraisalOrderQueue,
    assignAppraisalOrder,
    markAppraisalOrderInReview,
    completeAppraisalOrder,
    cancelAppraisalOrder,
  } = useUnderwritingHook();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lane, setLane] = useState(ALL);
  const [search, setSearch] = useState("");
  const [myQueue, setMyQueue] = useState(false);
  const [hideInactive, setHideInactive] = useState(() =>
    readHideInactive(HIDE_INACTIVE_KEY),
  );
  const [scope, setScope] = useState(SCOPE.ALL);
  // dealId → loan stage / closing date, joined from both pipeline lists.
  // null until loaded (or if both pipeline fetches fail) — nothing is hidden then.
  const [loanIndex, setLoanIndex] = useState(null);
  const [actingId, setActingId] = useState(null);
  // { mode, order } | null — confirmation dialog for an action.
  const [dialog, setDialog] = useState(null);

  useEffect(() => {
    writeHideInactive(HIDE_INACTIVE_KEY, hideInactive);
  }, [hideInactive]);

  // The queue and both pipelines load together so the stage filter is applied
  // before the first paint (no rows flashing in and then disappearing).
  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const queue = new Promise((resolve) => {
      getAppraisalOrderQueue({
        onSuccessFn: (rows) => {
          setOrders(Array.isArray(rows) ? rows : []);
          resolve();
        },
        onFailFn: (err) => {
          setError(
            err?.response?.data?.detail ||
              err?.message ||
              "Failed to load the queue.",
          );
          resolve();
        },
      });
    });
    const pipelines = fetchLoanIndex().then(setLoanIndex);
    Promise.all([queue, pipelines]).finally(() => setLoading(false));
  }, [getAppraisalOrderQueue]);

  useEffect(() => {
    if (canAccess) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccess]);

  // Patch a single order's row in place from an action response.
  const patchOrder = useCallback((updated) => {
    if (!updated) return;
    setOrders((prev) =>
      prev.map((o) =>
        orderKey(o) === orderKey(updated) ? { ...o, ...updated } : o,
      ),
    );
  }, []);

  const runAction = useCallback(
    (action, order, payload) => {
      const id = orderKey(order);
      setActingId(id);
      const handlers = {
        onSuccessFn: (updated) => {
          patchOrder(updated);
          enqueueSnackbar(ACTION_TOAST[action] || "Order updated", {
            variant: "success",
          });
          setDialog(null);
        },
        onFailFn: (err) => {
          if (err?.response?.status === 409) {
            enqueueSnackbar(
              "Someone else already updated this order — reloading the queue.",
              { variant: "warning" },
            );
            load();
            setDialog(null);
          } else {
            enqueueSnackbar(
              err?.response?.data?.detail || err?.message || "Action failed.",
              { variant: "error" },
            );
          }
        },
        onCompleteFn: () => setActingId(null),
      };
      if (action === "assign")
        assignAppraisalOrder({ orderId: id, ...handlers });
      else if (action === "in-review")
        markAppraisalOrderInReview({ orderId: id, ...handlers });
      else if (action === "complete")
        completeAppraisalOrder({ orderId: id, ...payload, ...handlers });
      else if (action === "cancel")
        cancelAppraisalOrder({ orderId: id, ...payload, ...handlers });
    },
    [
      assignAppraisalOrder,
      markAppraisalOrderInReview,
      completeAppraisalOrder,
      cancelAppraisalOrder,
      patchOrder,
      enqueueSnackbar,
      load,
    ],
  );

  // Every action confirms first — the dialog explains what's about to happen
  // and (for complete/cancel) collects the outcome/reason.
  const handleAction = useCallback((action, order) => {
    setDialog({ mode: action, order });
  }, []);

  // Every order stamped with its loan's stage + closing date.
  const rows = useMemo(
    () =>
      orders.map((o) =>
        withLoanStage(o, loanIndex, o?.dealPipeline === "Acquisitions"),
      ),
    [orders, loanIndex],
  );

  // The working set: every lane count, stat card and list reads from this, so
  // hidden loans drop out of the numbers too.
  const activeRows = useMemo(
    () => (hideInactive ? rows.filter((o) => !o.loanInactive) : rows),
    [rows, hideInactive],
  );
  const hiddenCount = rows.length - activeRows.length;

  // Top-row counts: per pipeline, after the dead / inactive switch.
  const scopeCounts = useMemo(
    () => ({
      [SCOPE.ALL]: activeRows.length,
      [SCOPE.PRECLOSE]: activeRows.filter((o) => inScope(o, SCOPE.PRECLOSE))
        .length,
      [SCOPE.ACQ]: activeRows.filter((o) => inScope(o, SCOPE.ACQ)).length,
    }),
    [activeRows],
  );

  // Everything below the top row (widgets, status pills, table) reads from this.
  const visibleRows = useMemo(
    () => activeRows.filter((o) => inScope(o, scope)),
    [activeRows, scope],
  );

  const laneCounts = useMemo(() => {
    const counts = { [ALL]: visibleRows.length };
    LANES.forEach(({ key }) => {
      if (key === ALL) return;
      counts[key] = visibleRows.filter((o) => o.status === key).length;
    });
    return counts;
  }, [visibleRows]);

  const stats = useMemo(() => {
    const by = (s) => visibleRows.filter((o) => o.status === s).length;
    return {
      queued: by("queued"),
      active: by("assigned") + by("in_review"),
      mine: visibleRows.filter(
        (o) =>
          (o.assignedTo || "").toLowerCase() === myEmail &&
          !TERMINAL.has(o.status),
      ).length,
      completed: by("completed"),
    };
  }, [visibleRows, myEmail]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return visibleRows.filter(
      (o) =>
        (lane === ALL || o.status === lane) &&
        (!myQueue || (o.assignedTo || "").toLowerCase() === myEmail) &&
        matchesSearch(o, q),
    );
  }, [visibleRows, lane, myQueue, myEmail, search]);

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Status",
        field: "status",
        width: 150,
        filter: "agSetColumnFilter",
        filterParams: {
          valueFormatter: (p) => STATUS_META[p.value]?.label || p.value,
        },
        cellRenderer: (p) => (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <StatusChip status={p.value} />
          </Box>
        ),
      },

      {
        headerName: "Loan",
        field: "loanNumber",
        width: 130,
        filter: "agTextColumnFilter",
        filterValueGetter: (p) =>
          p.data?.loanNumber != null ? String(p.data.loanNumber) : "",
        cellRenderer: (p) => {
          const dealId = dealKey(p.data?.dealId);
          const label = p.value != null ? `#${p.value}` : "—";
          if (!dealId) return <Box component="span">{label}</Box>;
          return (
            <Box
              component="a"
              href={`${
                p.data?.loanIsAcquisition ? "/acquisition" : "/loan"
              }/${dealId}`}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "5px",
                color: T.teal600,
                fontWeight: 600,
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {label}
              <OpenInNewRounded sx={{ fontSize: 13 }} />
            </Box>
          );
        },
      },
      correspondentColumnDef({ loaded: Boolean(loanIndex) }),
      ...loanStageColumnDefs({ loaded: Boolean(loanIndex) }),
      {
        headerName: "Subject property",
        field: "subjectPropertyAddress",
        flex: 1.4,
        minWidth: 200,
        filter: "agTextColumnFilter",
        cellRenderer: (p) => (
          <Box
            title={p.value || ""}
            sx={{
              color: T.ink900,
              fontWeight: 500,
              minWidth: 0,
              width: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {p.value || "—"}
          </Box>
        ),
      },
      {
        headerName: "Requested / due",
        colId: "requestedByDate",
        flex: 1,
        minWidth: 170,
        valueGetter: (p) => p.data?.requestedByDate || "",
        filter: "agDateColumnFilter",
        filterParams: { comparator: dateOnlyComparator },
        cellRenderer: (p) => {
          const due = p.data?.requestedByDate
            ? formatDate(p.data.requestedByDate, "")
            : "—";
          const by = p.data?.requestedBy;
          return (
            <Box sx={{ lineHeight: 1.25 }}>
              <Box sx={{ color: T.ink900, fontSize: 13 }}>{due}</Box>
              {by && (
                <Box sx={{ fontSize: 11.5, color: T.ink500 }}>by {by}</Box>
              )}
            </Box>
          );
        },
      },
      {
        headerName: "Assigned to",
        field: "assignedTo",
        flex: 1,
        minWidth: 160,
        filter: "agSetColumnFilter",
        filterParams: {
          valueFormatter: (p) =>
            !p.value
              ? "Unassigned"
              : p.value.toLowerCase() === myEmail
                ? "You"
                : p.value,
        },
        cellRenderer: (p) =>
          p.value ? (
            <Box component="span" sx={{ color: T.ink700 }}>
              {(p.value || "").toLowerCase() === myEmail ? "You" : p.value}
            </Box>
          ) : (
            <Box component="span" sx={{ color: T.ink300, fontStyle: "italic" }}>
              Unassigned
            </Box>
          ),
      },
      {
        headerName: "Outcome",
        field: "reviewOutcome",
        width: 170,
        filter: "agSetColumnFilter",
        filterParams: {
          valueFormatter: (p) =>
            p.value ? OUTCOME_LABEL[p.value] || p.value : "(None)",
        },
        cellRenderer: (p) =>
          p.value ? (
            <Box component="span" sx={{ color: T.ink700, fontSize: 12.5 }}>
              {OUTCOME_LABEL[p.value] || p.value}
            </Box>
          ) : (
            <Box component="span" sx={{ color: T.ink300 }}>
              —
            </Box>
          ),
      },
      {
        headerName: "Created",
        field: "createdAt",
        flex: 0.8,
        minWidth: 150,
        filter: "agDateColumnFilter",
        filterParams: { comparator: dateOnlyComparator },
        valueFormatter: (p) => (p.value ? formatDate(p.value, "") : "—"),
      },
    ],
    [myEmail, loanIndex],
  );

  if (!canAccess) return <NoAccessGate />;

  // Row/list click → open the master-detail split view for that order's loan.
  const handleSelect = (order) => {
    if (!order?.dealId) {
      enqueueSnackbar("This order isn't linked to a loan.", {
        variant: "warning",
      });
      return;
    }
    // Land the embedded drilldown on the Orders tab.
    navigate(
      `/appraisal-queue/${order.dealId}/orders?order=${orderKey(order)}`,
    );
  };

  const shared = {
    orders: rows,
    filtered,
    stats,
    laneCounts,
    lane,
    setLane,
    search,
    setSearch,
    myQueue,
    setMyQueue,
    hideInactive,
    setHideInactive,
    hiddenCount,
    scope,
    setScope,
    scopeCounts,
    loading,
    error,
    columnDefs,
    actingId,
  };

  return (
    <>
      <Routes>
        <Route
          index
          element={
            <FullQueueView
              {...shared}
              onRefresh={load}
              onSelect={handleSelect}
            />
          }
        />
        <Route
          path="outside/*"
          element={
            <OutsideOrdersQueue
              loanIndex={loanIndex}
              loanIndexLoading={loading}
            />
          }
        />
        <Route
          path=":id/*"
          element={
            <SplitQueueView
              {...shared}
              onAction={handleAction}
              onSelect={handleSelect}
              onClose={() => navigate("/appraisal-queue")}
            />
          }
        />
      </Routes>

      <ActionDialog
        mode={dialog?.mode}
        order={dialog?.order}
        submitting={Boolean(actingId) && Boolean(dialog)}
        onClose={() => setDialog(null)}
        onSubmit={(payload) => runAction(dialog.mode, dialog.order, payload)}
      />
    </>
  );
};

const ACTION_TOAST = {
  assign: "Order assigned to you",
  "in-review": "Marked in review",
  complete: "Review completed",
  cancel: "Order cancelled",
};

export default AppraisalQueueScreen;
